import { useQueries, useQuery } from '@tanstack/react-query'

import {
  choiceGroupKey,
  choiceGroupsKey,
  fetchChoiceGroup,
  fetchChoiceGroups,
} from '@/features/modifiers'
import type { ChoiceGroupDetail } from '@/features/modifiers'
import { translated } from '@/lib/api/common'

/** One choice a customer can pick on the menu — "Oat milk" in "Milk". */
export interface MenuChoice {
  /** The choice-group item's id: what the POS sends in a fired line's `modifiers`. */
  id: string
  name: string
}

/** A choice group and its choices, as the picker lists them under a heading. */
export interface MenuChoiceGroup {
  id: string
  name: string
  choices: MenuChoice[]
}

/**
 * Every choice on the menu, by group, for linking a recipe modifier to one.
 *
 * The link is the choice-group item's id: the order service sends each picked
 * choice's `choiceGroupItemId` as a modifier ref when a line is fired, and the
 * inventory service applies the recipe modifier whose `catalogModifierRef` is
 * that id. A modifier with no link never reaches a sale.
 *
 * The list has no items, so each group is read on its own — under the same
 * keys the Modifiers screen uses, so an edit there shows here.
 */
export function useMenuChoices() {
  const list = useQuery({
    queryKey: [...choiceGroupsKey, 'list'],
    queryFn: fetchChoiceGroups,
  })
  const groups = list.data ?? []

  const details = useQueries({
    queries: groups.map((group) => ({
      queryKey: choiceGroupKey(group.id),
      queryFn: () => fetchChoiceGroup(group.id),
    })),
    combine: toMenuGroups,
  })

  return {
    groups: details.groups,
    isLoading: list.isPending || details.isPending,
    error: list.error,
  }
}

/**
 * The loaded groups, as the picker lists them. At module level so TanStack Query
 * keeps the last result while nothing changed, as `itemsById` does.
 */
function toMenuGroups(results: { data?: ChoiceGroupDetail; isPending: boolean }[]): {
  groups: MenuChoiceGroup[]
  isPending: boolean
} {
  const groups = results
    .flatMap((result) => (result.data ? [result.data] : []))
    .map((group) => ({
      id: group.id,
      name: translated(group.name),
      choices: [...group.items]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => ({ id: item.id, name: translated(item.name) })),
    }))
    .filter((group) => group.choices.length > 0)

  return { groups, isPending: results.some((result) => result.isPending) }
}

/** The choice with this id, with its group's name, or `null` when the menu no longer has it. */
export function findMenuChoice(
  groups: readonly MenuChoiceGroup[],
  id: string | null,
): { group: string; choice: MenuChoice } | null {
  if (!id) return null

  for (const group of groups) {
    const choice = group.choices.find((candidate) => candidate.id === id)
    if (choice) return { group: group.name, choice }
  }

  return null
}
